import {useSelector} from 'react-redux';

import {getConfig} from '@/config/config';
import {BADGE_STATUSES} from '@/consts/loyalty/badgeStatuses';
import {getBadgeDetailsContent} from '@/services/content/appContent';
import {formatTime} from '@/services/plural';
import {selectLanguageCode} from '@/state/selectors/app';
import type {BadgeDetailsContentModel} from '@/types/aem';
import type {Badge} from '@/types/loyalty';
import {twx} from '@/utils/tailwindUtils';

const SECONDS_PER_DAY = 24 * 60 * 60;
const MS_PER_DAY = SECONDS_PER_DAY * 1000;

type BadgeTagProps = {
    badge: Badge;
    className?: string;
};

const BadgeTag = ({badge, className}: BadgeTagProps) => {
    const languageCode = useSelector(selectLanguageCode);
    const label = resolveBadgeTagName(
        badge,
        getBadgeDetailsContent(),
        languageCode,
        getConfig().badgeExpirationReminderDays
    );

    if (!label) return null;

    return (
        <span
            data-testid='badge-tag'
            className={twx(
                'flex items-center justify-center rounded-full bg-romance px-4 py-1',
                'font-bold text-12 text-secondary',
                className
            )}
        >
            {label}
        </span>
    );
};

function calendarDaysUntil(expireAt: Date, now: Date): number {
    const expireMidnight = new Date(expireAt.getFullYear(), expireAt.getMonth(), expireAt.getDate());
    const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return Math.round((expireMidnight.getTime() - nowMidnight.getTime()) / MS_PER_DAY);
}

export function resolveBadgeTagName(
    badge: Badge,
    content: BadgeDetailsContentModel,
    locale: string,
    reminderDays?: number
): string | undefined {
    if (badge.status === BADGE_STATUSES.COMPLETED) return undefined;

    const {newBadgeTagName, expiringBadgeTimeConfig} = content;

    if (badge.expireAt && expiringBadgeTimeConfig) {
        const now = new Date();
        const isExpired = badge.expireAt.getTime() <= now.getTime();
        const daysLeft = calendarDaysUntil(badge.expireAt, now);
        const withinReminderWindow = typeof reminderDays === 'number' && daysLeft <= reminderDays;

        if (!isExpired && withinReminderWindow) {
            const expiringLabel = formatTime(daysLeft * SECONDS_PER_DAY, expiringBadgeTimeConfig, locale, true);

            if (expiringLabel) return expiringLabel;
        }
    }

    if (badge.tagName) return badge.tagName;

    if (badge.isNewBadge && newBadgeTagName) return newBadgeTagName;

    return undefined;
}

export default BadgeTag;
